import { Link } from "react-router-dom";
import { ArrowRight, Broadcast, Lightning } from "@phosphor-icons/react";

import { SponsorShowcase } from "@/components/SponsorShowcase";
import { StoryCard } from "@/components/StoryCard";
import { VerticalVideoFrame } from "@/components/VerticalVideoFrame";
import { buildSiteSchemas, usePageSeo } from "@/hooks/usePageSeo";
import { Button } from "@/components/ui/button";
import { ENTITY_PAGE_LIST } from "@/utils/entityPages";
import { getTopStoryGroups } from "@/utils/newsroomCollections";
import { getStoryDisplayTitle } from "@/utils/storySections";
import { getYouTubeEmbedUrl } from "@/utils/videoEmbed";

const StorySection = ({ title, overline, route, stories = [] }) => (
  <section className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid={`home-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="mb-8 flex flex-wrap items-end justify-between gap-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">{overline}</p>
        <h2 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">{title}</h2>
      </div>
      {route ? (
        <Button asChild className="rounded-none border border-[#0A0A0A] bg-transparent px-4 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid={`home-section-link-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} variant="ghost">
          <Link to={route}>Open desk</Link>
        </Button>
      ) : null}
    </div>
    <div className="news-card-grid-compact">
      {stories.slice(0, 2).map((story) => <StoryCard key={story.id || story.slug} story={story} />)}
    </div>
  </section>
);

export const LandingPage = ({ siteData, feed }) => {
  const groups = getTopStoryGroups(feed);
  const breakingStories = (feed || []).filter((story) => story.section === "breaking");
  const heroStory = siteData?.featured_story || breakingStories[0] || feed?.[0];
  const topStories = (breakingStories.length ? breakingStories : (feed || [])).filter((story) => story.slug !== heroStory?.slug).slice(0, 3);
  const isLiveNow = siteData?.featured_livestream?.status === "live";
  const liveEmbedUrl = getYouTubeEmbedUrl(siteData?.featured_livestream?.youtube_url);

  usePageSeo({
    canonicalPath: "/",
    description: "Content Watch HQ is Houston’s independent digital newsroom for breaking news, public safety, investigations, public records, traffic, weather, video reports, and livestream coverage.",
    schema: buildSiteSchemas(),
    title: "Content Watch HQ | R.I.P. MAINSTREAM MEDIA",
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14" data-testid="landing-page">
      <section className="grid gap-8 border border-[#D9DCE1] bg-white p-6 shadow-[0_28px_80px_rgba(15,23,42,0.05)] lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-10 lg:p-10" data-testid="home-hero-section">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D21404]" data-testid="home-hero-overline">R.I.P. MAINSTREAM MEDIA</p>
          <h1 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A] sm:text-5xl" data-testid="home-hero-title">Houston News Now</h1>
          <p className="mt-5 max-w-3xl text-base leading-8 text-slate-700" data-testid="home-hero-description">We’re not trying to look like every legacy station. We’re building a Houston-first newsroom that moves faster, hits harder, and still gives people the coverage they expect.</p>
          <p className="mt-7 border-l-4 border-[#1338BE] pl-4 text-sm font-semibold uppercase tracking-[0.2em] text-[#1338BE]" data-testid="home-hero-secondary-copy">Your headquarters for news now.</p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="home-hero-primary-actions">
            <Button asChild className="rounded-none bg-[#1338BE] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="home-hero-breaking-button">
              <Link to="/breaking-news">Open Breaking News</Link>
            </Button>
            <Button asChild className="rounded-none border border-[#0A0A0A] bg-transparent px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="home-hero-submit-tip-button" variant="ghost">
              <Link to="/submit-a-tip">Submit a Tip</Link>
            </Button>
          </div>
          <div className="mt-8 border border-[#D9DCE1] bg-[#F8F9FA] p-5 sm:p-6" data-testid="federal-news-family-strip">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1338BE]">As of June 1, 2026</p>
            <p className="mt-2 text-sm leading-7 text-slate-700">We are now a part of the Federal News Network family; however, our mission has not changed. Just funding got better.</p>
          </div>
        </div>

        <div className="space-y-8" data-testid="home-hero-right-rail">
          <div className="border border-[#D9DCE1] bg-[#0A0A0A] p-5 text-white sm:p-6" data-testid="home-live-alert-card">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Live coverage</p>
            <h2 className="mt-3 font-[Montserrat] text-2xl font-bold uppercase tracking-[0.08em] text-white">{siteData?.featured_livestream?.title || "Live desk preparing"}</h2>
            <p className="mt-3 text-sm leading-7 text-slate-200">{isLiveNow ? "Live video is now active from YouTube and will move into Breaking News automatically after the stream ends." : siteData?.featured_livestream?.description || "Feature a livestream from the admin dashboard to promote it across the homepage and live desk."}</p>
            {liveEmbedUrl ? (
              <div className="mt-6 overflow-hidden border border-white/10 bg-black" data-testid="home-live-embed-frame">
                <div className="aspect-video w-full">
                  <iframe allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="h-full w-full" data-testid="home-live-embed" src={liveEmbedUrl} title={siteData?.featured_livestream?.title || "Live stream"} />
                </div>
              </div>
            ) : null}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="rounded-none bg-[#D21404] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid="home-live-page-button">
                <Link to="/live">
                  <Broadcast size={16} weight="bold" />
                  Open Live
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {isLiveNow ? (
        <section className="mt-10 border border-[#D21404] bg-[#D21404] p-5 text-white sm:p-6" data-testid="home-live-takeover-banner">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em]">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-white" />
            Live Now
          </p>
          <p className="mt-2 text-sm leading-7">A live YouTube stream is active right now and will roll into Breaking News automatically after the broadcast ends.</p>
        </section>
      ) : null}

      {siteData?.featured_sponsors?.length ? <section className="mt-12"><SponsorShowcase sponsors={siteData.featured_sponsors.slice(0, 3)} /></section> : null}

      <section className="mt-12 grid gap-8 xl:grid-cols-2" data-testid="home-desk-grid-primary">
        <StorySection overline="Breaking news coverage" route="/breaking-news" stories={topStories} title="Breaking News Coverage" />
        <StorySection overline="Crime & Public Safety" route="/crime" stories={[...groups.crime, ...groups.publicSafety]} title="Crime & Public Safety" />
      </section>

      <section className="mt-12 grid gap-8 xl:grid-cols-2" data-testid="home-desk-grid-secondary">
        <StorySection overline="Sports desk" route="/sports" stories={(feed || []).filter((story) => story.section === "sports")} title="Sports" />
        <StorySection overline="What’s New desk" route="/whats-new" stories={(feed || []).filter((story) => story.section === "editorial")} title="What’s New" />
      </section>

      <section className="mt-12 grid gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]" data-testid="home-desk-grid-tertiary">
        <StorySection overline="Politics" route="/politics" stories={groups.politics} title="Politics" />
        <StorySection overline="Video reports" route="/videos" stories={groups.videos} title="Videos" />
      </section>

      <section className="mt-12 grid gap-8 border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] lg:grid-cols-[minmax(0,1fr)_360px] lg:p-8" data-testid="home-investigations-panel">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid="home-investigations-overline">Investigations + source portal</p>
          <h2 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]" data-testid="home-investigations-title">Locked-down source intake plus entity watch pages</h2>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-700" data-testid="home-investigations-description">We’ve opened the investigations desk, a protected whistleblower portal with case tracking, and permanent entity pages for major Houston institutions.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="rounded-none bg-[#1338BE] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="home-investigations-open-button">
              <Link to="/investigations">Open Investigations</Link>
            </Button>
            <Button asChild className="rounded-none border border-[#0A0A0A] bg-transparent px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="home-investigations-portal-button" variant="ghost">
              <Link to="/submit-a-tip">Open Secure Portal</Link>
            </Button>
          </div>
        </div>
        <div className="space-y-3" data-testid="home-entity-link-grid">
          {ENTITY_PAGE_LIST.map((entity) => (
            <Link key={entity.key} className="block border border-[#D9DCE1] bg-[#F8F9FA] px-4 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-slate-800 transition-colors hover:border-[#1338BE] hover:text-[#1338BE]" data-testid={`home-entity-link-${entity.key}`} to={entity.route}>
              {entity.title}
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
};